import { useCallback, useEffect, useRef, useState } from 'react'
import { useLogto, type IdTokenClaims } from '@logto/react'

import {
  E2E_STUB_ACCESS_TOKEN,
  E2E_STUB_USER,
  isE2EAuthStub,
} from '@/auth/e2e-stub'

export interface AppUser {
  id: string
  email: string
  name: string | null
  avatarUrl: string | null
}

const apiResource = import.meta.env.VITE_API_URL
const redirectUri =
  import.meta.env.VITE_REDIRECT_URI ??
  (typeof window !== 'undefined' ? `${window.location.origin}/callback` : '/callback')

interface UseUserReturn {
  isAuthenticated: boolean
  isLoading: boolean
  user: AppUser | null
  signIn: () => Promise<void>
  signOut: () => Promise<void>
  getAccessToken: () => Promise<string>
}

const STUB_RESULT: UseUserReturn = {
  isAuthenticated: true,
  isLoading: false,
  user: { ...E2E_STUB_USER, avatarUrl: null },
  signIn: async () => {},
  signOut: async () => {},
  getAccessToken: async () => E2E_STUB_ACCESS_TOKEN,
}

/**
 * Stub used by Playwright CI runs (VITE_E2E_AUTH_STUB=1). Reports a fixed
 * authenticated user so route guards short-circuit straight to the
 * authenticated tree without booting Logto. The real `useUserReal` below
 * runs unchanged in dev/staging/prod builds.
 */
function useUserStub(): UseUserReturn {
  return STUB_RESULT
}

/**
 * Convenience hook combining Logto auth state with helpers for sign-in /
 * sign-out and a synchronous user view. Drop-in replacement for the
 * previous WorkOS hook — same surface (`isAuthenticated`, `isLoading`,
 * `user`, `signIn`, `signOut`, `getAccessToken`) so callers don't need
 * to change.
 *
 * The Logto SDK does not memoize the functions returned from useLogto(),
 * so each render produces fresh references. We capture them in refs and
 * read `.current` inside our effect/callbacks. That keeps our hook's
 * effect deps and returned callback identities stable across renders —
 * without it, a state update from any sibling hook (e.g. useMe()) would
 * re-fire the claims effect, which would setClaims, which would re-render,
 * which would re-fire the effect, → React error #185 (infinite loop).
 */
function useUserReal(): UseUserReturn {
  const {
    isAuthenticated,
    isLoading,
    signIn: kitSignIn,
    signOut: kitSignOut,
    getAccessToken,
    getIdTokenClaims,
  } = useLogto()

  const claimsGetterRef = useRef(getIdTokenClaims)
  const signInRef = useRef(kitSignIn)
  const signOutRef = useRef(kitSignOut)
  const accessTokenRef = useRef(getAccessToken)

  // Sync refs to the latest values from useLogto() after every render. The
  // ref-sync effect is declared BEFORE the claims-fetch effect, so when
  // isAuthenticated flips, the refs are updated before the fetch effect
  // reads them in the same commit's effect-phase pass.
  useEffect(() => {
    claimsGetterRef.current = getIdTokenClaims
    signInRef.current = kitSignIn
    signOutRef.current = kitSignOut
    accessTokenRef.current = getAccessToken
  })

  const [claims, setClaims] = useState<IdTokenClaims | undefined>(undefined)
  const hasFetchedForCurrentSessionRef = useRef(false)

  useEffect(() => {
    if (!isAuthenticated) {
      hasFetchedForCurrentSessionRef.current = false
      return
    }
    if (hasFetchedForCurrentSessionRef.current) return
    hasFetchedForCurrentSessionRef.current = true

    let cancelled = false
    claimsGetterRef
      .current()
      .then((c) => {
        if (cancelled) return
        setClaims((prev) => {
          if (
            prev &&
            c &&
            prev.sub === c.sub &&
            prev.email === c.email &&
            prev.name === c.name &&
            (prev as { picture?: unknown }).picture ===
              (c as { picture?: unknown }).picture
          ) {
            return prev
          }
          return c
        })
      })
      .catch(() => {
        // ID-token claims are best-effort; UI still works without them.
        hasFetchedForCurrentSessionRef.current = false
      })
    return () => {
      cancelled = true
    }
  }, [isAuthenticated])

  const signIn = useCallback(async () => {
    await signInRef.current(redirectUri)
  }, [])

  const signOut = useCallback(async () => {
    await signOutRef.current(typeof window !== 'undefined' ? window.location.origin : undefined)
  }, [])

  const tokenGetter = useCallback(async (): Promise<string> => {
    const token = await accessTokenRef.current(apiResource)
    return token ?? ''
  }, [])

  const user: AppUser | null =
    isAuthenticated && claims
      ? {
          id: claims.sub,
          email: claims.email ?? '',
          name: claims.name ?? claims.email ?? null,
          avatarUrl:
            typeof (claims as { picture?: unknown }).picture === 'string'
              ? (claims as { picture: string }).picture
              : null,
        }
      : null

  return {
    isAuthenticated,
    isLoading,
    user,
    signIn,
    signOut,
    getAccessToken: tokenGetter,
  }
}

// Module-level pick so dead branches tree-shake out of production bundles
// when VITE_E2E_AUTH_STUB is unset. Callers see the exact same API in
// either mode.
export const useUser: () => UseUserReturn = isE2EAuthStub
  ? useUserStub
  : useUserReal
